import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FrontendTeam } from '@/services/teams.api';
import RichTextRenderer from '@/components/base/RichTextRenderer';

interface TeamDetailModalProps {
  member: FrontendTeam | null;
  onClose: () => void;
}

export default function TeamDetailModal({ member, onClose }: TeamDetailModalProps) {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  useEffect(() => {
    if (!member) return;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [member, onClose]);

  if (!member) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      
      {/* Poster Container - 横向布局，图片完整显示 */}
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-3xl shadow-2xl bg-white flex flex-col md:flex-row min-h-[500px] md:min-h-[600px]">
        
        {/* 左侧：完整照片展示区域 - 占据固定比例 */}
        <div className="w-full md:w-3/5 h-64 md:h-auto bg-gradient-to-br from-[#0A2540] to-[#2C7DA0] relative overflow-hidden flex-shrink-0">
          {/* 照片容器 - 完整显示，自适应高度 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-full flex items-center justify-center p-4 md:p-6">
              {member.photo ? (
                <img
                  src={member.photo}
                  alt={isZh ? member.name : member.nameEn}
                  className="w-full h-full object-contain object-center"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))'
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-4">
                      <i className="ri-user-line text-5xl text-white/30" />
                    </div>
                    <p className="text-white/40 text-sm">{isZh ? member.name : member.nameEn}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* 底部渐变遮罩，增强可读性 */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/40 via-black/10 to-transparent pointer-events-none" />
          
          {/* 装饰元素 */}
          <div className="absolute top-4 left-4 md:bottom-8 md:left-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold">
              <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full">
                <span className="text-xs font-bold text-[#2C7DA0]">GT</span>
              </div>
              <span>Global Tech Roundtable Alliance</span>
            </div>
          </div>
          
          {/* 角色标签 */}
          <div className="absolute top-4 right-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2C7DA0]/40 backdrop-blur-sm border border-[#7EC8E3]/30 text-white text-sm font-medium">
              <i className={member.role === 'executive' ? 'ri-user-settings-line' : 'ri-shield-star-line'} />
              <span>{isZh ? (member.role === 'executive' ? '执行团队' : '顾问委员会') : (member.role === 'executive' ? 'Executive Team' : 'Advisory Board')}</span>
            </div>
          </div>
        </div>

        {/* 右侧：详细信息区域 */}
        <div className="w-full md:w-2/5 bg-white flex flex-col flex-1 min-w-0">
          {/* 顶部渐变条 */}
          <div className="h-2 bg-gradient-to-r from-[#0A2540] to-[#2C7DA0] flex-shrink-0" />
          
          {/* 可滚动的内容区域 */}
          <div className="flex-1 overflow-y-auto p-6 md:p-8 min-h-0">
            {/* 姓名 */}
            <div className="mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-2 leading-tight">
                {isZh ? member.name : member.nameEn}
              </h2>
              <p className="text-[#2C7DA0] text-xl font-medium mb-1">
                {isZh ? member.title : member.titleEn}
              </p>
              <p className="text-[#0A2540]/60 text-base">
                {isZh ? member.institution : member.institutionEn}
              </p>
            </div>

            {/* 分隔线 */}
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-gradient-to-r from-[#2C7DA0]/30 to-transparent" />
              <div className="w-2 h-2 rounded-full bg-[#2C7DA0]/50" />
              <div className="h-px flex-1 bg-gradient-to-l from-[#2C7DA0]/30 to-transparent" />
            </div>

            {/* 个人简介 */}
            <div className="mb-6">
              <h3 className="text-[#0A2540] font-bold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center bg-[#2C7DA0]/10 rounded">
                  <i className="ri-user-3-line text-[#2C7DA0] text-xs" />
                </div>
                <span>{t('teamPage.modal.biography')}</span>
              </h3>
              <div className="bg-[#F7F9FC] rounded-xl p-5">
                <RichTextRenderer
                  content={isZh ? member.bio : member.bioEn}
                  className="text-[#0A2540]/70 text-sm leading-relaxed"
                />
              </div>
            </div>

            {/* LinkedIn 按钮 */}
            {member.linkedin && (
              <div className="flex items-center justify-center pt-4 border-t border-[#0A2540]/8">
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-xl text-sm font-medium transition-all group"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-linkedin-fill" />
                  </div>
                  <span>{t('teamPage.modal.viewLinkedIn')}</span>
                  <div className="w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-external-link-line text-xs" />
                  </div>
                </a>
              </div>
            )}
          </div>
          
          {/* 底部渐变条 */}
          <div className="h-1 bg-gradient-to-r from-[#2C7DA0] to-[#0A2540]" />
        </div>

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all backdrop-blur-sm cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl" />
        </button>

        {/* 装饰角标 */}
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-[#2C7DA0]/10 to-transparent rounded-tr-full" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#7EC8E3]/10 to-transparent rounded-tl-full" />
      </div>
    </div>
  );
}
